import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectDreamTeamCardData_MLB, selectNewSelectionCardData, selectPreSelectedCardId } from '../../../../store/showcase/showcase.selector';
import { removeShowcaseCard, setCardImgsWereAdded, setIsViewingNewSelection, setNewSelectionCardData, setPreselectedCardId, setPreselectedCardType, setSelectedCardId, updateReduxPositionData } from '../../../../store/showcase/showcase.action';
import { deleteShowcaseCard, removeShowcaseCardFromPosition, saveNewCardToShowcase } from '../../../../utils/firebase/showcase';
import { selectUser } from '../../../../store/user/user.selector';

// styles
import {
    BelowCard,
    CardSelectionViewContainer,
    CenteredCard,
    EditorActionRow,
    EditorField,
    ReplacementWarningText,
} from './CardSelectionView.styles';

// components
import CardDisplayLarge from '../card-display-large/CardDisplayLarge';
import CardOptions from '../card-display-large/components/card-options/CardOptions';
import PrimaryBtn from '../../../../components/btns/primary-btn/PrimaryBtn';
import IconBtn from '../../../../components/btns/icon-btn/IconBtn';
import InputDropdown from '../../../../components/input-dropdown/InputDropdown';
import closeIcon from '../../../../assets/system/icons/close-icon.svg';
import editIcon from '../../../../assets/system/icons/edit-icon.svg';
import saveIcon from '../../../../assets/system/icons/save-icon.svg';

const SHOWCASE_POSITIONS = [
    { id: 'field-DH', label: 'DH' },
    { id: 'field-C', label: 'C' },
    { id: 'field-1B', label: '1B' },
    { id: 'field-2B', label: '2B' },
    { id: 'field-3B', label: '3B' },
    { id: 'field-SS', label: 'SS' },
    { id: 'field-LF', label: 'LF' },
    { id: 'field-CF', label: 'CF' },
    { id: 'field-RF', label: 'RF' },
    { id: 'rotation-SP1', label: 'SP 1' },
    { id: 'rotation-SP2', label: 'SP 2' },
    { id: 'rotation-SP3', label: 'SP 3' },
    { id: 'rotation-SP4', label: 'SP 4' },
    { id: 'rotation-SP5', label: 'SP 5' },
    { id: 'bullpen-LRP1', label: 'LRP 1' },
    { id: 'bullpen-LRP2', label: 'LRP 2' },
    { id: 'bullpen-MRP1', label: 'MRP 1' },
    { id: 'bullpen-MRP2', label: 'MRP 2' },
    { id: 'bullpen-SU1', label: 'SU 1' },
    { id: 'bullpen-SU2', label: 'SU 2' },
    { id: 'bullpen-CL', label: 'CL' },
];

const IMAGE_ORIENTATIONS = [
    { id: 'vertical', label: 'Vertical' },
    { id: 'horizontal', label: 'Horizontal' },
];

export default function ShowcaseSelectionView({ cardData }) {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const user = useSelector(selectUser);
    const newSelectionCardData = useSelector(selectNewSelectionCardData);
    const preSelectedCardId = useSelector(selectPreSelectedCardId);
    const dreamTeamCardData_MLB = useSelector(selectDreamTeamCardData_MLB);

    const isExistingCard = !!newSelectionCardData?.id;

    const cardAtSelectedPosition =
        preSelectedCardId && dreamTeamCardData_MLB?.[preSelectedCardId]
            ? dreamTeamCardData_MLB[preSelectedCardId]
            : null;
    const isEditingSamePosition =
        isExistingCard && newSelectionCardData?.positionId === preSelectedCardId;
    const showPositionReplacementWarning =
        !!cardAtSelectedPosition && !isEditingSamePosition;

    const replacementWarningPositionLabel =
        SHOWCASE_POSITIONS.find((p) => p.id === preSelectedCardId)?.label ?? '';
    const replacementWarningCardName = (() => {
        const names = cardAtSelectedPosition?.name;
        if (Array.isArray(names) && names.length > 0) {
            return names.join(' / ');
        }
        return 'The current card';
    })();

    // Image upload state - initialize from Redux state (uploadedImage = blob, imageURL = Storage URL)
    const [frontImg, setFrontImg] = useState(newSelectionCardData.uploadedImage || newSelectionCardData.imageURL || null);
    const [frontOrientation, setFrontOrientation] = useState(newSelectionCardData.imageOrientation || "vertical");
    const [validationMessage, setValidationMessage] = useState(null);
    const [confirmingRemoval, setConfirmingRemoval] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [baseline] = useState(() => ({
        positionId: preSelectedCardId || newSelectionCardData.positionId || null,
        orientation: newSelectionCardData.imageOrientation || 'vertical',
        imageURL: newSelectionCardData.uploadedImage || newSelectionCardData.imageURL || null,
    }));

    const hasChanges = (preSelectedCardId || null) !== baseline.positionId
        || frontOrientation !== baseline.orientation
        || (frontImg || null) !== baseline.imageURL;
    const canSave = !isSaving && (hasChanges || !isExistingCard);

    useEffect(() => {
        if (frontImg !== null) {
            dispatch(setCardImgsWereAdded(true));
        } else {
            dispatch(setCardImgsWereAdded(false));
        }
    }, [dispatch, frontImg]);

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Clear any previous validation message
            setValidationMessage(null);

            // Validate file size (2MB = 2 * 1024 * 1024 bytes)
            const maxSize = 2 * 1024 * 1024; // 2MB
            if (file.size > maxSize) {
                setValidationMessage('File size must be less than 2MB');
                event.target.value = '';
                return;
            }

            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                setValidationMessage('Only JPG and PNG files are allowed');
                event.target.value = '';
                return;
            }

            const imageUrl = URL.createObjectURL(file);
            setFrontImg(imageUrl);
            setValidationMessage(null); // Clear validation message on successful upload

            // Update Redux state with uploaded image data
            dispatch(setNewSelectionCardData({
                ...newSelectionCardData,
                uploadedImage: imageUrl,
                imageOrientation: frontOrientation
            }));
        }
        // Clear the input value to allow selecting the same file again
        event.target.value = '';
    };

    const handleRemoveImage = () => {
        setFrontImg(null);
        setFrontOrientation("vertical"); // Reset to default orientation
        setValidationMessage(null); // Clear validation message when removing image

        // Update Redux state to remove image data
        dispatch(setNewSelectionCardData({
            ...newSelectionCardData,
            uploadedImage: null,
            imageOrientation: 'vertical'
        }));
    };

    const handleRemoveCard = async () => {
        const cardId = newSelectionCardData?.id
            || dreamTeamCardData_MLB?.[preSelectedCardId]?.id;
        const imageURL = newSelectionCardData?.imageURL
            || dreamTeamCardData_MLB?.[preSelectedCardId]?.imageURL;
        const positionIdForRemoval = newSelectionCardData?.positionId || preSelectedCardId;
        if (!cardId && !positionIdForRemoval) return;
        try {
            if (cardId) {
                await deleteShowcaseCard(user.uid, cardId, imageURL);
                dispatch(removeShowcaseCard(cardId));
            } else {
                await removeShowcaseCardFromPosition(user.uid, positionIdForRemoval);
            }
            dispatch(setPreselectedCardId(null));
            dispatch(setPreselectedCardType(null));
            dispatch(setIsViewingNewSelection(false));
            navigate('/', {
                replace: true,
                state: { showcaseView: location.state?.showcaseView === 'field' ? 'field' : 'carousel' },
            });
        } catch (error) {
            console.error('Error removing card from showcase:', error);
        }
    };

    const handleCancelEdit = () => {
        setFrontImg(baseline.imageURL);
        setFrontOrientation(baseline.orientation);
        setValidationMessage(null);
        setConfirmingRemoval(null);
        dispatch(setPreselectedCardId(baseline.positionId));
        dispatch(setNewSelectionCardData({
            ...newSelectionCardData,
            uploadedImage: null,
            imageURL: typeof baseline.imageURL === 'string' && baseline.imageURL.startsWith('blob:')
                ? newSelectionCardData.imageURL
                : baseline.imageURL,
            imageOrientation: baseline.orientation,
        }));
        setIsEditing(false);
    };

    const handleBack = () => {
        const showcaseView = location.state?.showcaseView;
        dispatch(setIsViewingNewSelection(false));
        if (showcaseView === 'carousel' || showcaseView === 'field') {
            if (showcaseView === 'carousel') {
                dispatch(setPreselectedCardId(null));
                dispatch(setPreselectedCardType(null));
            }
            navigate('/', { replace: true, state: { showcaseView } });
            return;
        }
        if (newSelectionCardData.name.length === 0) {
            dispatch(setSelectedCardId(null));
            dispatch(setPreselectedCardType(null));
            dispatch(setPreselectedCardId(null));
            return;
        }
        navigate(-1);
    };

    const handleSave = async () => {
        if (!canSave) return;
        setIsSaving(true);
        try {
            const { finalCardData } = await saveNewCardToShowcase(user.uid, newSelectionCardData, preSelectedCardId || null);
            dispatch(updateReduxPositionData(preSelectedCardId || null, finalCardData));
            dispatch(setIsViewingNewSelection(false));
            const openedFrom = location.state?.showcaseView;
            if (openedFrom === 'field' && preSelectedCardId) {
                dispatch(setPreselectedCardType(preSelectedCardId.startsWith('field-') ? 'field' : 'bullpen'));
                navigate('/', { replace: true, state: { showcaseView: 'field' } });
            } else if (openedFrom === 'carousel') {
                dispatch(setPreselectedCardId(null));
                dispatch(setPreselectedCardType(null));
                navigate('/', { replace: true, state: { showcaseView: 'carousel' } });
            } else {
                navigate('/', {
                    replace: true,
                    state: { showcaseView: preSelectedCardId ? 'field' : 'carousel' },
                });
            }
        } catch (error) {
            console.error('Error saving card to showcase:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const confirmRemoval = (kind) => {
        if (confirmingRemoval !== kind) {
            setConfirmingRemoval(kind);
            return;
        }
        setConfirmingRemoval(null);
        if (kind === 'image') handleRemoveImage();
        else handleRemoveCard();
    };

    const handleOrientationChange = (orientation) => {
        setFrontOrientation(orientation);

        // Update Redux state with new orientation
        dispatch(setNewSelectionCardData({
            ...newSelectionCardData,
            imageOrientation: orientation
        }));
    };

    return (
        <CardSelectionViewContainer>
            <CardOptions />
            <CenteredCard>
            <CardDisplayLarge
                size={"L"}
                posIsDisplayed={false}
                cardData={{
                    ...(newSelectionCardData.name.length === 0 ? cardData : newSelectionCardData),
                    imageURL: frontImg,
                    imageOrientation: frontOrientation
                }}
                footer={(
                    <BelowCard>
                        {isEditing && validationMessage && (
                            <h3 style={{
                                color: '#ff6b6b',
                                fontSize: '14px',
                                margin: 0,
                                textAlign: 'center'
                            }}>
                                {validationMessage}
                            </h3>
                        )}
                        {isEditing && showPositionReplacementWarning && replacementWarningPositionLabel && (
                            <ReplacementWarningText>
                                {`${replacementWarningCardName} at ${replacementWarningPositionLabel} will return to the carousel.`}
                            </ReplacementWarningText>
                        )}
                        <EditorActionRow>
                            {isEditing ? (
                                <IconBtn label="CANCEL" onClick={handleCancelEdit}>
                                    <img src={closeIcon} alt="" />
                                </IconBtn>
                            ) : (
                                <IconBtn label="BACK" onClick={handleBack}>
                                    <img src={closeIcon} alt="" />
                                </IconBtn>
                            )}
                            {isEditing && (
                                <EditorField>
                                    <InputDropdown
                                        label="Position"
                                        placeholder="Select Position"
                                        value={preSelectedCardId || ''}
                                        onChange={(val) => dispatch(setPreselectedCardId(val || null))}
                                        className="widget-bg"
                                    >
                                        {SHOWCASE_POSITIONS.map(({ id, label }) => (
                                            <option key={id} value={id}>{label}</option>
                                        ))}
                                    </InputDropdown>
                                    {!frontImg ? (
                                        <div
                                            onClick={() => document.getElementById('file-upload').click()}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <PrimaryBtn title="UPLOAD IMAGE" width="fill" />
                                            <input
                                                style={{ display: 'none' }}
                                                id="file-upload"
                                                type="file"
                                                accept="image/jpeg,image/jpg,image/png"
                                                onChange={handleImageUpload}
                                            />
                                        </div>
                                    ) : (
                                        <PrimaryBtn
                                            type="critical"
                                            title={confirmingRemoval === 'image' ? 'CONFIRM DELETION' : 'REMOVE IMAGE'}
                                            onClick={() => confirmRemoval('image')}
                                            width="fill"
                                        />
                                    )}
                                </EditorField>
                            )}
                            {isEditing && (
                                <EditorField>
                                    <InputDropdown
                                        label="Orientation"
                                        value={frontOrientation}
                                        onChange={(val) => {
                                            if (val === 'vertical' || val === 'horizontal') {
                                                handleOrientationChange(val);
                                            }
                                        }}
                                        className="widget-bg"
                                    >
                                        {IMAGE_ORIENTATIONS.map(({ id, label: orientationLabel }) => (
                                            <option key={id} value={id}>{orientationLabel}</option>
                                        ))}
                                    </InputDropdown>
                                    {isExistingCard && (
                                        <PrimaryBtn
                                            type="critical"
                                            title={confirmingRemoval === 'card' ? 'CONFIRM DELETION' : 'REMOVE CARD'}
                                            onClick={() => confirmRemoval('card')}
                                            width="fill"
                                        />
                                    )}
                                </EditorField>
                            )}
                            {isEditing ? (
                                <IconBtn label="SAVE" onClick={handleSave} disabled={!canSave}>
                                    <img src={saveIcon} alt="" />
                                </IconBtn>
                            ) : (
                                <IconBtn label="EDIT" onClick={() => setIsEditing(true)}>
                                    <img src={editIcon} alt="" />
                                </IconBtn>
                            )}
                        </EditorActionRow>
                    </BelowCard>
                )}
            />
            </CenteredCard>
        </CardSelectionViewContainer>
    );
}